import { Badge, BadgeProps } from '@/components/ui/badge';
import { CopyButton } from '@/components/ui/copy-button';
import { formatDate, cn } from '@/lib/utils';
import { ReactNode, useId, useMemo } from 'react';

const numberFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
});

type DisplayIdProps = {
  value?: string;
};

export const DisplayId = ({ value }: DisplayIdProps) => (
  <code className="inline-flex items-center justify-start rounded-md text-xs">
    {value || '-'}
    {value && <CopyButton text={value} />}
  </code>
);

type DisplayTimestampProps = {
  timestamp: string;
};

export const DisplayTimestamp = ({ timestamp }: DisplayTimestampProps) => {
  const formattedTimestamp = useMemo(() => {
    try {
      return formatDate(timestamp);
    } catch (error) {
      console.error('Error formatting timestamp', error);
      return timestamp || '-';
    }
  }, [timestamp]);

  return <time dateTime={timestamp}>{formattedTimestamp}</time>;
};

type DisplayCodeProps = {
  value?: string;
};

const DisplayCode = ({ value }: DisplayCodeProps) => (
  <div className="relative rounded-md bg-muted p-2">
    <pre className="overflow-x-auto p-2">
      <code className="inline-flex items-center justify-start rounded-md text-xs">
        {value}
      </code>
    </pre>
    <div className="absolute right-2 top-2">
      {value && <CopyButton text={value} />}
    </div>
  </div>
);

type DisplayStatusProps = {
  value?: string;
};

const DisplayStatus = ({ value }: DisplayStatusProps) => {
  const variant = useMemo<BadgeProps['variant']>(() => {
    if (!value) {
      return undefined;
    }

    const normalizedStatus = value.toLowerCase();

    if (['active', 'linked'].includes(normalizedStatus)) {
      return 'success';
    }

    if (['failed', 'error'].includes(normalizedStatus)) {
      return 'destructive';
    }

    return 'secondary';
  }, [value]);

  return <Badge variant={variant}>{value}</Badge>;
};

type DisplayNumberProps = {
  value?: number;
};

const DisplayNumber = ({ value }: DisplayNumberProps) => (
  <div className="text-sm font-medium">
    {value === undefined ? '-' : numberFormatter.format(value)}
  </div>
);

type DisplayValueProps =
  | {
      label: ReactNode;
      value?: string;
      type?: 'text' | 'datetime' | 'id' | 'code' | 'status';
      className?: string;
    }
  | {
      label: ReactNode;
      value?: number;
      type?: 'number';
      className?: string;
    };

export const DisplayValue = ({
  label,
  value,
  type,
  className,
}: DisplayValueProps) => {
  const formattedValue = useMemo(() => {
    switch (type) {
      case 'datetime':
        return <DisplayTimestamp timestamp={value} />;
      case 'id':
        return <DisplayId value={value} />;
      case 'code':
        return <DisplayCode value={value} />;
      case 'status':
        return <DisplayStatus value={value} />;
      case 'number':
        return <DisplayNumber value={value} />;
      case 'text':
      default:
        return value;
    }
  }, [type, value]);

  const id = useId();

  return (
    <div className={cn('grid grid-cols-1 gap-1', className)}>
      <div className="text-sm font-medium" id={id}>
        {label}
      </div>
      <div className="overflow-x-auto text-sm" aria-labelledby={id}>
        {formattedValue}
      </div>
    </div>
  );
};
